import gsap from "@lib/gsap";
import { onDestroy, onTrack, onView } from "@/modules/_";
import { prepLineDraw, playLineDraw, cleanLineDraw } from "@utils/line-draw";

/**
 * Vertical parallax on images only (px at full scroll, track value in [-1, 1]).
 * Keep these modest; if clip edges peek through, raise `.u-img-cover` scale in Webflow.
 */
const DRIFT: [string, number][] = [
  ["cc-top-left", -28],
  ["cc-top-right", 32],
  ["cc-mid-left", -36],
  ["cc-bottom-center", 22],
];

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const containers = Array.from(
    element.querySelectorAll<HTMLElement>(".scatter-cta_img")
  );

  const bottomCenter = containers.find((el) =>
    el.classList.contains("cc-bottom-center")
  );
  if (bottomCenter) gsap.set(bottomCenter, { xPercent: -50 });

  const items = containers
    .map((container) => {
      const img = container.querySelector<HTMLElement>("img");
      if (!img) return null;

      const match = DRIFT.find(([cls]) => container.classList.contains(cls));
      const speed = match?.[1] ?? 24;

      return {
        container,
        img,
        setY: gsap.quickTo(img, "y", {
          duration: 0.8,
          ease: "power3.out",
        }),
        speed,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (items.length) {
    onTrack(element, {
      bounds: [-1, 1],
      top: "bottom",
      bottom: "top",
      callback: (value) => {
        for (const { setY, speed } of items) {
          setY(value * speed);
        }
      },
    });
  }

  const prepared = prepLineDraw(element);

  if (prepared) {
    let drawn = false;
    const bg = element.querySelector<HTMLElement>(".scatter-cta_bg");

    onView(bg ?? element.parentElement ?? element, {
      threshold: 0.25,
      autoStart: true,
      callback: ({ isIn }) => {
        if (isIn && !drawn) {
          drawn = true;
          playLineDraw(prepared);
        }
      },
    });
  }

  onDestroy(() => {
    const allEls = items.flatMap(({ container, img }) => [container, img]);
    gsap.killTweensOf(allEls);
    allEls.forEach((el) => gsap.set(el, { clearProps: "transform" }));
    if (prepared) cleanLineDraw(prepared.path);
  });
}
